import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react'
import Hero from '../components/Hero'
import FeatureCard from '../components/FeatureCard'
import ProgramCard from '../components/ProgramCard'
import CTASection from '../components/CTASection'
import ContactForm from '../components/ContactForm'
import SectionHeader from '../components/SectionHeader'
import { STATEMENT, MISSION, FEATURES, PROGRAMS, SITE, CONTACT } from '../content/siteContent'

export default function HomePage() {
  // Only show available programs on homepage (max 3)
  const featuredPrograms = PROGRAMS.items.filter((p) => p.available).slice(0, 3)

  return (
    <main>
      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <Hero />

      {/* ─── Statement ────────────────────────────────────────────── */}
      <section id="statement" className="section-pad bg-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-round font-bold tracking-[0.15em] uppercase text-brand-blue mb-3">
              Our Heart
            </p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-navy mb-6 leading-snug">
              {STATEMENT.headline}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {STATEMENT.body}
            </p>
            <div className="mt-8 inline-block border-l-4 border-brand-gold pl-5 text-left">
              <p className="text-base italic text-brand-navy font-display">{MISSION.quote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us ────────────────────────────────────────── */}
      <section id="why-us" className="section-pad bg-brand-ivory">
        <div className="section-container">
          <SectionHeader
            eyebrow="Why Grace Pointe"
            headline="Built on Trust, Guided by Love"
            subheadline="Everything we do is designed to give your child the best possible start — and give you peace of mind while they're in our care."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Featured Programs ────────────────────────────────────── */}
      <section id="programs-preview" className="section-pad bg-white">
        <div className="section-container">
          <SectionHeader
            eyebrow="Programs &amp; Enrollment"
            headline="Care for Every Stage"
            subheadline="From infants to preschoolers, we offer age-appropriate programs grounded in love, safety, and learning."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {featuredPrograms.map((p) => (
              <ProgramCard key={p.id} {...p} />
            ))}
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-brand-sky/10 border border-brand-sky/20 text-brand-blue font-round font-semibold text-sm rounded-full px-5 py-2 mb-5">
              🎉 {PROGRAMS.enrollmentNote}
            </div>
            <br />
            <Link to="/programs" className="btn-secondary">
              View All Programs
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Mission strip ────────────────────────────────────────── */}
      <section className="bg-brand-navy py-14 md:py-16">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">
                {MISSION.headline}
              </p>
              <blockquote className="text-xl md:text-2xl lg:text-3xl font-display font-bold italic text-white leading-snug">
                {MISSION.quote}
              </blockquote>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {MISSION.pillars.map((pillar) => (
                <div key={pillar.title} className="bg-white/10 border border-white/10 rounded-xl3 p-5 hover:bg-white/15 transition-colors">
                  <p className="text-white font-display font-bold mb-1">{pillar.title}</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{pillar.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Gallery preview ──────────────────────────────────────── */}
      <section id="gallery-preview" className="section-pad bg-brand-ivory">
        <div className="section-container">
          <SectionHeader eyebrow="Gallery" headline="A Peek Inside Our World" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-8">
            {[
              '/images/IMG_7373.jpg',
              '/images/IMG_7537.jpg',
              '/images/IMG_7559.jpg',
              '/images/IMG_7579.jpg',
              '/images/IMG_7614.jpg',
              '/images/IMG_7654.jpg',
              '/images/IMG_7692.jpg',
              '/images/IMG_7714.jpg',
            ].map((src, i) => (
              <div key={src} className="aspect-square rounded-xl2 overflow-hidden shadow-card">
                <img
                  src={src}
                  alt={`Grace Pointe children and activities photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/gallery" className="btn-secondary">
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA Band ─────────────────────────────────────────────── */}
      <CTASection />

      {/* ─── Contact Section ──────────────────────────────────────── */}
      <section id="contact" className="section-pad bg-white">
        <div className="section-container">
          <SectionHeader
            eyebrow="Contact Us"
            headline={CONTACT.headline}
            subheadline={CONTACT.subheadline}
          />
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">

            {/* Contact info panel */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-brand-navy font-display font-bold text-xl mb-4">Visit or Call Us</h3>
                <ul className="space-y-4" role="list">
                  <li>
                    <a href={`tel:${SITE.contact.phone.replace(/\D/g,'')}`} className="flex items-center gap-3 text-slate-600 hover:text-brand-navy transition-colors group">
                      <div className="w-10 h-10 rounded-xl bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <Phone size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Phone</p>
                        <p className="font-semibold text-brand-navy">{SITE.contact.phone}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${SITE.contact.email}`} className="flex items-center gap-3 text-slate-600 hover:text-brand-navy transition-colors group">
                      <div className="w-10 h-10 rounded-xl bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <Mail size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Email</p>
                        <p className="font-semibold text-brand-navy break-all text-sm">{SITE.contact.email}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(SITE.contact.address.display)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-slate-600 hover:text-brand-navy transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <MapPin size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Address</p>
                        <p className="font-semibold text-brand-navy text-sm">{SITE.contact.address.display}</p>
                      </div>
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-sky/15 flex items-center justify-center text-brand-blue flex-shrink-0">
                      <Clock size={18} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hours</p>
                      <p className="font-semibold text-brand-navy text-sm">{SITE.hours.days}</p>
                      <p className="text-slate-500 text-sm">{SITE.hours.open} – {SITE.hours.close}</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Map link */}
              <a
                href={CONTACT.mapPlaceholder}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-brand-cream border border-brand-sky/20 rounded-xl2 p-4 hover:border-brand-sky transition-colors group"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-brand-navy">Get Directions</p>
                  <p className="text-xs text-slate-500">{SITE.contact.address.display}</p>
                </div>
                <ExternalLink size={16} className="text-brand-blue group-hover:text-brand-navy" aria-hidden="true" />
              </a>

              {/* Parent portal */}
              <a
                href={SITE.procare.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-brand-navy text-white rounded-xl2 p-4 hover:bg-brand-blue transition-colors group"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold">Current Families</p>
                  <p className="text-xs text-slate-300">Access the Procare Parent Portal</p>
                </div>
                <ExternalLink size={16} aria-hidden="true" />
              </a>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="card">
                <h3 className="text-brand-navy font-display font-bold text-xl mb-6">Send Us a Message</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
